import { useRef, useState } from "react"
import type { MouseEvent, ReactNode } from "react"
import { IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFileCsv, faPrint } from "@fortawesome/pro-light-svg-icons"
import { faArrowDownToLine } from "@fortawesome/pro-regular-svg-icons"

import { LocalizedStrict } from "../../../../localization/components/AppLocalized"

type Props = Readonly<{
    disabled?: boolean
    buttonClassName?: string
    printLabel: ReactNode
    exportLabel: ReactNode
    onPrint: () => void
    onExportCsv: () => void
}>

export function PrintExportMenu({ disabled, buttonClassName, printLabel, exportLabel, onPrint, onExportCsv }: Props) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    // printing while the menu fades out would put it on the sheet
    const afterMenuClosedRef = useRef<(() => void) | null>(null)

    const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)
    const handleClose = () => setAnchorEl(null)

    const handlePrint = () => {
        afterMenuClosedRef.current = onPrint
        handleClose()
    }

    const handleExportCsv = () => {
        handleClose()
        onExportCsv()
    }

    const handleMenuExited = () => {
        afterMenuClosedRef.current?.()
        afterMenuClosedRef.current = null
    }

    return (
        <>
            <LocalizedStrict id="export-button-export-text" attrs={{ title: true }}>
                <Tooltip title="Export">
                    <span>
                        <LocalizedStrict id="export-button-export-text" attrs={{ "aria-label": true }}>
                            <IconButton
                                aria-label="Export"
                                aria-haspopup="menu"
                                aria-expanded={Boolean(anchorEl)}
                                disabled={disabled}
                                className={buttonClassName}
                                onClick={handleOpen}
                            >
                                <FontAwesomeIcon icon={faArrowDownToLine} />
                            </IconButton>
                        </LocalizedStrict>
                    </span>
                </Tooltip>
            </LocalizedStrict>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                TransitionProps={{ onExited: handleMenuExited }}
            >
                <MenuItem onClick={handlePrint}>
                    <ListItemIcon>
                        <FontAwesomeIcon icon={faPrint} />
                    </ListItemIcon>
                    {printLabel}
                </MenuItem>
                <MenuItem onClick={handleExportCsv}>
                    <ListItemIcon>
                        <FontAwesomeIcon icon={faFileCsv} />
                    </ListItemIcon>
                    {exportLabel}
                </MenuItem>
            </Menu>
        </>
    )
}
